import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useStore } from 'dashboard/composables/store';

export const FILTER_LIST_OPTIONS = [
  {
    key: 'status',
    label: 'Status',
    icon: '📊',
    color: 'blue',
    valueKey: 'name',
    labelKey: 'name',
  },
  {
    key: 'priority',
    label: 'Prioridade',
    icon: '⚡',
    color: 'green',
    valueKey: 'name',
    labelKey: 'name',
  },
  {
    key: 'contact_id',
    label: 'Contato',
    icon: '📧',
    color: 'pink',
    valueKey: 'id',
    labelKey: 'name',
  },
  {
    key: 'team_id',
    label: 'Time',
    icon: '👥',
    color: 'purple',
    valueKey: 'id',
    labelKey: 'name',
  },
  {
    key: 'assignee_id',
    label: 'Responsável',
    icon: '👤',
    color: 'orange',
    valueKey: 'id',
    labelKey: 'name',
  },
  {
    key: 'custom_attributes',
    label: 'Atributos Personalizados',
    icon: '🏷️',
    color: 'indigo',
    valueKey: 'id',
    labelKey: 'name',
  },
];

export function useTicketFilters({
  view = 'list',
  storageKey = 'ticket_filters',
  excludeTeamFilter = false,
  disablePagination = false,
  getBoardId = null,
} = {}) {
  const store = useStore();
  const filters = computed(
    () => store.getters['tickets/getFiltersByView'](view) || {}
  );

  // Filter data from store
  const statuses = computed(() => store.getters['tickets/getStatuses'] || []);
  const priorities = computed(
    () => store.getters['tickets/getPriorities'] || []
  );
  const users = computed(() => store.getters['agents/getAgents'] || []);
  const teams = computed(() => store.getters['teams/getMyTeams'] || []);
  const contacts = computed(() => store.getters['contacts/getContacts'] || []);
  const ticketAttributes = computed(
    () => store.getters['attributes/getTicketAttributes'] || []
  );

  // Filter list options computed (excludes team_id if excludeTeamFilter is true)
  const filterListOptions = computed(() => {
    if (excludeTeamFilter) {
      return FILTER_LIST_OPTIONS.filter(option => option.key !== 'team_id');
    }
    return FILTER_LIST_OPTIONS;
  });

  // Filter data computed
  const filterData = computed(() => ({
    status: statuses.value || [],
    priority: priorities.value || [],
    contact_id: contacts.value || [],
    team_id: teams.value || [],
    assignee_id: users.value || [],
    custom_attributes: (ticketAttributes.value || []).map(attr => ({
      id: attr.id,
      name: attr.attributeDisplayName,
      values: attr.attributeValues.map(value => ({
        id: value,
        name: value,
      })),
    })),
  }));

  // Visible filters configuration
  const STORAGE_KEY_FILTERS = `${storageKey}_visible_filters`;
  const defaultVisibleFilters = computed(() => {
    const allFilters = filterListOptions.value.map(filter => filter.key);
    if (excludeTeamFilter) {
      return allFilters.filter(key => key !== 'team_id');
    }
    return allFilters;
  });

  const getStoredFilters = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_FILTERS);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (excludeTeamFilter) {
          return parsed.filter(key => key !== 'team_id');
        }
        return parsed;
      }
    } catch (e) {
      // Silently ignore localStorage errors.
    }
    return defaultVisibleFilters.value;
  };

  const visibleFilters = ref(getStoredFilters());

  // Watch visible filters and save to localStorage
  watch(
    visibleFilters,
    newFilters => {
      try {
        localStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify(newFilters));
      } catch (e) {
        // Silently ignore localStorage errors.
      }
    },
    { deep: true }
  );

  // Search query
  const searchQuery = ref('');
  const searchTimeout = ref(null);
  const searchInputRef = ref(null);
  const saveFiltersTimeout = ref(null);
  const isRemovingTeamFilter = ref(false);

  // Selected filters computed
  const selectedFilters = computed(() => {
    const tags = [];

    if (searchQuery.value.trim()) {
      tags.push({
        type: 'search',
        key: 'search',
        value: searchQuery.value.trim(),
        label: `"${searchQuery.value.trim()}"`,
        color: 'blue',
        icon: '🔍',
      });
    }

    filterListOptions.value.forEach(option => {
      // Skip custom_attributes as they're handled separately
      if (option.key === 'custom_attributes') return;
      // Skip team_id if excludeTeamFilter is true
      if (excludeTeamFilter && option.key === 'team_id') return;

      const filterValues = filters.value[option.key] || [];
      const options = filterData.value[option.key] || [];

      filterValues.forEach(value => {
        const optionItem = options.find(opt => opt[option.valueKey] === value);
        if (optionItem) {
          tags.push({
            type: option.key,
            key: option.key,
            value: value,
            label: optionItem[option.labelKey],
            color: option.color,
            icon: option.icon,
          });
        }
      });
    });

    // Handle custom attributes filters
    const customAttributeFilters = filters.value.custom_attributes || {};
    Object.keys(customAttributeFilters).forEach(attributeKey => {
      const filterValue = customAttributeFilters[attributeKey];
      const attribute = ticketAttributes.value.find(
        attr => attr.attributeKey === attributeKey
      );

      if (attribute && filterValue) {
        let label = attribute.attributeDisplayName;

        // Format label based on filter value structure
        if (Array.isArray(filterValue)) {
          label = `${attribute.attributeDisplayName}: ${filterValue.join(', ')}`;
        } else if (
          typeof filterValue === 'object' &&
          filterValue.operator &&
          filterValue.value !== undefined
        ) {
          const operatorLabels = {
            equals: '=',
            not_equals: '≠',
            greater_than: '>',
            less_than: '<',
            greater_than_or_equal: '≥',
            less_than_or_equal: '≤',
            contains: 'contém',
            not_contains: 'não contém',
            starts_with: 'começa com',
            ends_with: 'termina com',
          };
          const opLabel =
            operatorLabels[filterValue.operator] || filterValue.operator;
          label = `${attribute.attributeDisplayName} ${opLabel} ${filterValue.value}`;
        }

        tags.push({
          type: 'custom_attribute',
          key: 'custom_attributes',
          attributeKey: attributeKey,
          value: filterValue,
          label: label,
          color: 'indigo',
          icon: '🏷️',
        });
      }
    });

    return tags;
  });

  // Fetch tickets with filters
  const fetchTickets = async (params = {}) => {
    const requestParams = { ...params };

    if (disablePagination) {
      requestParams.per_page = 'all';
    }

    if (searchQuery.value.trim()) {
      requestParams.search = searchQuery.value.trim();
    }

    if (typeof getBoardId === 'function') {
      const boardId = getBoardId();
      if (boardId != null && boardId !== '') {
        requestParams.board_id = boardId;
      }
    }

    // Remove team_id from filters if excludeTeamFilter is true
    if (excludeTeamFilter) {
      // Remove from store to prevent it from being applied
      const currentFilters = { ...filters.value };
      if (currentFilters.team_id && currentFilters.team_id.length > 0) {
        isRemovingTeamFilter.value = true;
        const filtersWithoutTeam = { ...currentFilters };
        delete filtersWithoutTeam.team_id;
        await store.dispatch('tickets/setFilters', {
          view,
          filters: filtersWithoutTeam,
        });
        await nextTick();
        isRemovingTeamFilter.value = false;
      }
      // Pass team_id: null to explicitly exclude it from the request
      requestParams.team_id = null;
    }

    await store.dispatch('tickets/getTickets', {
      ...requestParams,
      filterView: view,
    });
  };

  // Save filters to backend
  function saveFiltersToBackend(immediate = false) {
    if (saveFiltersTimeout.value) {
      clearTimeout(saveFiltersTimeout.value);
    }

    const save = async () => {
      await store.dispatch('tickets/saveFiltersToBackend', {
        view,
        searchQuery: searchQuery.value,
      });
    };

    if (immediate) {
      save();
    } else {
      saveFiltersTimeout.value = setTimeout(save, 1000);
    }
  }

  // Search handler
  const handleSearch = () => {
    if (searchTimeout.value) {
      clearTimeout(searchTimeout.value);
    }

    searchTimeout.value = setTimeout(async () => {
      await fetchTickets();
      saveFiltersToBackend();
      await nextTick();

      // Maintain focus on search input after search completes
      if (searchInputRef.value && searchInputRef.value.$el) {
        const inputElement = searchInputRef.value.$el.querySelector('input');
        if (inputElement) {
          // Use requestAnimationFrame to ensure DOM is fully updated before focusing
          requestAnimationFrame(() => {
            inputElement.focus();
          });
        }
      }
    }, 500);
  };

  // Filter handlers
  const getSelectedItems = (filterKey, options) => {
    const selectedValues = filters.value[filterKey] || [];
    const option = filterListOptions.value.find(o => o.key === filterKey);
    if (!option) return [];

    return options.filter(optionItem => {
      const value = optionItem[option.valueKey];
      return selectedValues.includes(value);
    });
  };

  const handleItemSelect = (filterKey, item) => {
    const currentFilters = { ...filters.value };
    const currentValues = currentFilters[filterKey] || [];
    const option = filterListOptions.value.find(o => o.key === filterKey);
    if (!option) return;

    const value = item[option.valueKey];
    const index = currentValues.indexOf(value);

    if (index > -1) {
      currentValues.splice(index, 1);
    } else {
      currentValues.push(value);
    }

    const newFilters = { ...currentFilters };
    if (currentValues.length > 0) {
      newFilters[filterKey] = currentValues;
    } else {
      delete newFilters[filterKey];
    }

    store.dispatch('tickets/setFilters', { view, filters: newFilters });
    fetchTickets();
    saveFiltersToBackend();
  };

  const handleCustomAttributeFilter = async customFilterData => {
    const currentFilters = { ...filters.value };
    const customAttributeFilters = {
      ...(currentFilters.custom_attributes || {}),
    };

    if (
      customFilterData.value === null ||
      (Array.isArray(customFilterData.value) &&
        customFilterData.value.length === 0)
    ) {
      delete customAttributeFilters[customFilterData.attributeKey];
    } else {
      customAttributeFilters[customFilterData.attributeKey] =
        customFilterData.value;
    }

    // Create a new filters object, explicitly handling custom_attributes
    const newFilters = { ...currentFilters };

    if (Object.keys(customAttributeFilters).length > 0) {
      newFilters.custom_attributes = customAttributeFilters;
    } else {
      // Explicitly set to empty object so the mutation can clear it
      newFilters.custom_attributes = {};
    }

    store.dispatch('tickets/setFilters', { view, filters: newFilters });

    // Wait for the store update to complete before fetching
    await nextTick();
    fetchTickets();
    saveFiltersToBackend();
  };

  const getCustomAttributeFilterValue = attributeKey => {
    return filters.value.custom_attributes?.[attributeKey] || null;
  };

  const removeFilter = filter => {
    // Handle search filter
    if (filter.key === 'search' || filter.type === 'search') {
      searchQuery.value = '';
      fetchTickets();
      saveFiltersToBackend();
      return;
    }

    // Handle custom attributes - check if it has attributeKey (which only custom attributes have)
    if (filter.attributeKey) {
      handleCustomAttributeFilter({
        attributeKey: filter.attributeKey,
        value: null,
      });
      return;
    }

    // Handle regular filters (status, priority, etc.)
    const filterKey = filter.key || filter.type;
    const value = filter.value;
    const currentFilters = { ...filters.value };
    const currentValues = currentFilters[filterKey] || [];
    const index = currentValues.indexOf(value);

    if (index > -1) {
      currentValues.splice(index, 1);

      const newFilters = { ...currentFilters };
      if (currentValues.length > 0) {
        newFilters[filterKey] = currentValues;
      } else {
        delete newFilters[filterKey];
      }

      store.dispatch('tickets/setFilters', { view, filters: newFilters });
      fetchTickets();
      saveFiltersToBackend();
    }
  };

  const handleClearFilters = () => {
    store.dispatch('tickets/clearFilters', { view });
    searchQuery.value = '';
    fetchTickets();
    saveFiltersToBackend();
  };

  // Initialize filters from backend
  const initializeFilters = async () => {
    try {
      const savedData = await store.dispatch('tickets/loadFiltersFromBackend', {
        view,
      });
      if (savedData.filters) {
        if (savedData.searchQuery) {
          searchQuery.value = savedData.searchQuery;
        }
        // Remove team_id filter if excludeTeamFilter is true
        if (excludeTeamFilter && savedData.filters.team_id) {
          const newFilters = { ...savedData.filters };
          delete newFilters.team_id;
          store.dispatch('tickets/setFilters', { view, filters: newFilters });
          saveFiltersToBackend(true);
        }
      } else {
        store.dispatch('tickets/clearFilters', { view });
        searchQuery.value = '';
      }
      // Ensure the first fetch after hydration uses both restored filters and text search.
      await fetchTickets();
    } catch {
      store.dispatch('tickets/clearFilters', { view });
      searchQuery.value = '';
      await fetchTickets();
    }
  };

  // Load filter data
  const loadFilterData = async () => {
    // Load all filter data in parallel to reduce number of requests
    const results = await Promise.allSettled([
      store.dispatch('tickets/getStatuses'),
      store.dispatch('tickets/getPriorities'),
      store.dispatch('teams/get'),
      store.dispatch('agents/get'),
      store.dispatch('contacts/get', { withTickets: true }),
      store.dispatch('attributes/get'),
    ]);

    return results;
  };

  // Cleanup on unmount
  onBeforeUnmount(() => {
    if (saveFiltersTimeout.value) {
      clearTimeout(saveFiltersTimeout.value);
    }
    saveFiltersToBackend(true);
  });

  return {
    // Data
    filterListOptions: filterListOptions,
    filterData,
    visibleFilters,
    searchQuery,
    searchInputRef,
    selectedFilters,
    ticketAttributes,

    // Methods
    fetchTickets,
    handleSearch,
    getSelectedItems,
    handleItemSelect,
    handleCustomAttributeFilter,
    getCustomAttributeFilterValue,
    removeFilter,
    handleClearFilters,
    refreshTickets: () => fetchTickets(),
    initializeFilters,
    loadFilterData,
  };
}
